/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Styles for the SuperAdmin tenant-data page and wizards (prefix `td-`).
 * Render once per host: <style>{tenantDataStyles}</style>.
 * TenantDataPage renders it; any other host of these components (e.g. the
 * org-detail card, prefix `tdc-`) must render it too.
 *
 * Class inventory (Tasks 15-18 use only these):
 *  layout      td-page td-header td-header-icon td-title td-subtitle td-toolbar
 *              td-tabs td-tab (.active | [aria-selected=true]) td-card td-card-title
 *              td-card-desc td-section-title td-nav td-actions td-empty td-loading
 *  wizard      td-steps td-step (.active .done) td-step-dot td-step-label
 *              td-mode-grid td-mode-card (--selected --danger | [aria-checked=true])
 *              td-mode-title td-mode-desc td-categories td-category (--disabled) td-note
 *  forms       td-field td-label td-input td-hint td-upload td-radio-group td-radio
 *  data        td-stats td-stat td-stat-label td-stat-value td-table-wrap
 *              td-preview-table td-history-table td-preview-group td-subtotal
 *              td-num td-pagination td-kv td-manifest
 *  feedback    td-banner (--danger --warning --info --success) td-badge (--pending
 *              --exporting --purging --restoring --completed --completed_with_warnings
 *              --failed) td-issues (--error --warning) td-issues-title td-issue-list
 *              td-issue td-issue-message td-issue-loc
 *  confirm     td-confirm (--danger) td-confirm-label td-confirm-input
 *              td-confirm-hint-slot td-confirm-hint (--match --mismatch)
 *  progress    td-progress td-progress-head td-progress-phase td-progress-label
 *              td-progress-bar td-progress-meta td-progress-error td-anim-in
 */
export const tenantDataStyles = `
  .td-page {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-6);
    color: var(--color-text-primary);
    font-family: var(--font-body);
  }
  .td-header { display: flex; align-items: center; gap: var(--space-4); }
  .td-header-icon {
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 48px; height: 48px;
    border-radius: var(--radius-lg);
    background: var(--color-brand-primary);
    color: var(--color-text-on-brand);
  }
  .td-title { margin: 0; font-family: var(--font-heading); font-size: 1.5rem; font-weight: 700; color: var(--color-text-primary); }
  .td-subtitle { margin: var(--space-1) 0 0; font-size: 0.875rem; color: var(--color-text-secondary); }
  .td-toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--space-4); }
  .td-toolbar > * { flex: 1 1 320px; min-width: 0; }

  .td-tabs { display: flex; gap: var(--space-1); border-bottom: 1px solid var(--color-border-subtle); overflow-x: auto; }
  .td-tab {
    appearance: none; background: none; border: none;
    border-bottom: 2px solid transparent;
    padding: var(--space-3) var(--space-4);
    font: inherit; font-size: 0.875rem; font-weight: 600;
    color: var(--color-text-secondary);
    cursor: pointer; white-space: nowrap;
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .td-tab:hover { color: var(--color-text-primary); }
  .td-tab.active, .td-tab[aria-selected="true"] { color: var(--color-brand-primary); border-bottom-color: var(--color-brand-primary); }
  .td-tab:focus-visible { outline: 2px solid var(--color-border-focus); outline-offset: 2px; }

  .td-card {
    display: flex; flex-direction: column; gap: var(--space-4);
    padding: var(--space-6);
    background: var(--color-bg-elevated);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-sm);
  }
  .td-card-title, .td-section-title { margin: 0; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; color: var(--color-text-primary); }
  .td-card-desc, .td-note { margin: 0; font-size: 0.875rem; line-height: 1.5; color: var(--color-text-secondary); }
  .td-nav { display: flex; justify-content: space-between; align-items: center; gap: var(--space-3); flex-wrap: wrap; }
  .td-actions { display: flex; gap: var(--space-2); justify-content: flex-end; flex-wrap: wrap; }
  .td-empty, .td-loading { padding: var(--space-6); text-align: center; font-size: 0.875rem; color: var(--color-text-muted); }

  .td-steps { display: flex; gap: var(--space-2); margin: 0; padding: 0; list-style: none; overflow-x: auto; }
  .td-step { display: flex; align-items: center; gap: var(--space-2); flex: 1; min-width: 0; font-size: 0.8125rem; color: var(--color-text-muted); }
  .td-step-dot {
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 28px; height: 28px;
    border-radius: var(--radius-full);
    border: 2px solid var(--color-border-default);
    background: var(--color-bg-secondary);
    font-size: 0.75rem; font-weight: 700;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  }
  .td-step-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .td-step.active { color: var(--color-text-primary); font-weight: 600; }
  .td-step.active .td-step-dot { border-color: var(--color-brand-primary); background: var(--color-brand-primary); color: var(--color-text-on-brand); }
  .td-step.done .td-step-dot { border-color: var(--color-success); background: var(--color-success); color: var(--color-text-inverse); }

  .td-mode-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--space-4); }
  .td-mode-card {
    display: flex; flex-direction: column; gap: var(--space-2);
    padding: var(--space-4);
    text-align: left; font: inherit; color: inherit;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-lg);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .td-mode-card:hover { border-color: var(--color-border-strong); }
  .td-mode-card:focus-visible { outline: 2px solid var(--color-border-focus); outline-offset: 2px; }
  .td-mode-card--selected, .td-mode-card[aria-checked="true"] { border-color: var(--color-brand-primary); box-shadow: 0 0 0 1px var(--color-brand-primary); }
  .td-mode-card--danger.td-mode-card--selected, .td-mode-card--danger[aria-checked="true"] { border-color: var(--color-error); box-shadow: 0 0 0 1px var(--color-error); }
  .td-mode-title { margin: 0; font-size: 0.9375rem; font-weight: 700; color: var(--color-text-primary); }
  .td-mode-desc { margin: 0; font-size: 0.8125rem; line-height: 1.5; color: var(--color-text-secondary); }

  .td-categories { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: var(--space-3); }
  .td-category {
    display: flex; align-items: center; gap: var(--space-2);
    padding: var(--space-3);
    font-size: 0.875rem;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  .td-category input { accent-color: var(--color-brand-primary); }
  .td-category--disabled { opacity: 0.6; cursor: not-allowed; }

  .td-field { display: flex; flex-direction: column; gap: var(--space-1-5); }
  .td-label { font-size: 0.8125rem; font-weight: 600; color: var(--color-text-secondary); }
  .td-input {
    width: 100%;
    padding: var(--space-2-5) var(--space-3);
    font: inherit; font-size: 0.875rem;
    color: var(--color-text-primary);
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .td-input:focus { outline: none; border-color: var(--color-border-focus); box-shadow: 0 0 0 3px var(--color-brand-glow); }
  .td-input[aria-invalid="true"] { border-color: var(--color-border-error); }
  .td-hint { margin: 0; font-size: 0.75rem; color: var(--color-text-muted); }
  .td-upload { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-3); }
  .td-radio-group { display: flex; flex-direction: column; gap: var(--space-2); }
  .td-radio { display: flex; align-items: flex-start; gap: var(--space-2); font-size: 0.875rem; cursor: pointer; }
  .td-radio input { accent-color: var(--color-brand-primary); margin-top: var(--space-0-5); }

  .td-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--space-3); }
  .td-stat { display: flex; flex-direction: column; gap: var(--space-1); padding: var(--space-3) var(--space-4); background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md); }
  .td-stat-label { font-size: 0.75rem; color: var(--color-text-muted); }
  .td-stat-value { font-family: var(--font-mono); font-size: 1.25rem; font-weight: 700; color: var(--color-text-primary); }

  .td-table-wrap { width: 100%; overflow-x: auto; border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); }
  .td-preview-table, .td-history-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
  .td-preview-table th, .td-history-table th {
    position: sticky; top: 0;
    padding: var(--space-2-5) var(--space-3);
    text-align: left; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--color-text-muted);
    background: var(--color-bg-secondary);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .td-preview-table td, .td-history-table td { padding: var(--space-2-5) var(--space-3); border-bottom: 1px solid var(--color-border-subtle); color: var(--color-text-primary); vertical-align: middle; }
  .td-preview-table tr:last-child td, .td-history-table tr:last-child td { border-bottom: none; }
  .td-history-table tbody tr:hover { background: var(--color-bg-hover); }
  .td-preview-group th, .td-preview-group td { background: var(--color-bg-tertiary); font-weight: 700; color: var(--color-text-primary); text-transform: none; letter-spacing: 0; }
  .td-subtotal { font-weight: 700; }
  .td-num { text-align: right; font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
  .td-pagination { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-3); font-size: 0.8125rem; color: var(--color-text-secondary); }

  .td-kv { display: grid; grid-template-columns: max-content 1fr; gap: var(--space-1-5) var(--space-4); margin: 0; font-size: 0.8125rem; }
  .td-kv dt { color: var(--color-text-muted); }
  .td-kv dd { margin: 0; color: var(--color-text-primary); min-width: 0; overflow-wrap: anywhere; }
  .td-kv code, .td-issue-loc { font-family: var(--font-mono); font-size: 0.75rem; padding: var(--space-0-5) var(--space-1-5); border-radius: var(--radius-sm); background: var(--color-bg-tertiary); color: var(--color-text-secondary); }
  .td-manifest { padding: var(--space-4); background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); }

  .td-banner {
    display: flex; flex-wrap: wrap; align-items: flex-start; gap: var(--space-2) var(--space-3);
    padding: var(--space-3) var(--space-4);
    font-size: 0.875rem; line-height: 1.5;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
  }
  .td-banner--danger { background: var(--color-error-light); border-color: var(--color-error); color: var(--color-error-dark); }
  .td-banner--warning { background: var(--color-warning-light); border-color: var(--color-warning); color: var(--color-warning-dark); }
  .td-banner--info { background: var(--color-info-light); border-color: var(--color-info); color: var(--color-info-dark); }
  .td-banner--success { background: var(--color-success-light); border-color: var(--color-success); color: var(--color-success-dark); }

  .td-badge {
    display: inline-flex; align-items: center; gap: var(--space-1-5);
    padding: var(--space-0-5) var(--space-2);
    font-size: 0.75rem; font-weight: 600; white-space: nowrap;
    border-radius: var(--radius-full);
  }
  .td-badge--pending { background: var(--color-bg-tertiary); color: var(--color-text-secondary); }
  .td-badge--exporting, .td-badge--purging, .td-badge--restoring { background: var(--color-info-light); color: var(--color-info-dark); }
  .td-badge--exporting::before, .td-badge--purging::before, .td-badge--restoring::before {
    content: ''; width: 6px; height: 6px;
    border-radius: var(--radius-full);
    background: currentColor;
    animation: td-pulse 1.2s ease-in-out infinite;
  }
  .td-badge--completed { background: var(--color-success-light); color: var(--color-success-dark); }
  .td-badge--completed_with_warnings { background: var(--color-warning-light); color: var(--color-warning-dark); }
  .td-badge--failed { background: var(--color-error-light); color: var(--color-error-dark); }

  .td-issues { padding: var(--space-3) var(--space-4); border: 1px solid transparent; border-radius: var(--radius-md); font-size: 0.8125rem; }
  .td-issues--error { background: var(--color-error-light); border-color: var(--color-error); color: var(--color-error-dark); }
  .td-issues--warning { background: var(--color-warning-light); border-color: var(--color-warning); color: var(--color-warning-dark); }
  .td-issues-title { margin: 0 0 var(--space-2); font-weight: 700; }
  .td-issue-list { display: flex; flex-direction: column; gap: var(--space-1-5); margin: 0; padding-left: var(--space-5); }
  .td-issue { line-height: 1.5; }
  .td-issue-message { margin-right: var(--space-2); }

  .td-confirm { display: flex; flex-direction: column; gap: var(--space-2); }
  .td-confirm-label { font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); }
  .td-confirm-input { font-family: var(--font-mono); }
  .td-confirm--danger .td-confirm-input:focus { border-color: var(--color-error); box-shadow: 0 0 0 3px var(--color-error-light); }
  .td-confirm-hint-slot { margin: 0; min-height: 1.25rem; font-size: 0.75rem; }
  .td-confirm-hint--match { color: var(--color-success-dark); }
  .td-confirm-hint--mismatch { color: var(--color-error); }

  .td-progress { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); }
  .td-progress-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; }
  .td-progress-phase { display: flex; align-items: baseline; gap: var(--space-2); margin: 0; font-size: 0.875rem; }
  .td-progress-label { color: var(--color-text-muted); }
  .td-progress-bar { width: 100%; }
  .td-progress-error { font-family: var(--font-mono); font-size: 0.8125rem; overflow-wrap: anywhere; }

  .td-anim-in { animation: td-fade-in 0.25s ease both; }
  @keyframes td-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
  @keyframes td-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

  @media (max-width: 640px) {
    .td-page { padding: var(--space-4); gap: var(--space-4); }
    .td-header { align-items: flex-start; }
    .td-title { font-size: 1.25rem; }
    .td-card { padding: var(--space-4); }
    .td-mode-grid, .td-categories, .td-stats { grid-template-columns: 1fr; }
    .td-step-label { display: none; }
    .td-step.active .td-step-label { display: inline; }
    .td-nav { flex-direction: column-reverse; align-items: stretch; }
    .td-nav > * { width: 100%; }
    .td-actions { justify-content: stretch; }
    .td-table-wrap { -webkit-overflow-scrolling: touch; }
    .td-preview-table, .td-history-table { min-width: 560px; }
    .td-pagination { justify-content: space-between; }
    .td-kv { grid-template-columns: 1fr; }
    .td-progress { padding: var(--space-3); }
  }

  @media (prefers-reduced-motion: reduce) {
    .td-page *, .td-page *::before, .td-page *::after,
    [class^="td-"], [class*=" td-"], [class^="td-"]::before, [class*=" td-"]::before {
      animation: none !important;
      transition: none !important;
    }
  }

  /* ── Task 16: page shell, tabs, history table, backup panel ───────────── */
  .td-shell {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  .td-shell-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }
  .td-shell-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: var(--radius-lg);
    background: var(--color-brand-primary);
    color: var(--color-text-on-brand);
    flex-shrink: 0;
  }
  .td-shell-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-text-primary);
  }
  .td-shell-subtitle {
    margin: var(--space-1) 0 0;
    color: var(--color-text-secondary);
  }
  .td-shell-picker {
    max-width: 480px;
  }
  .td-shell-tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--color-border-subtle);
    overflow-x: auto;
  }
  .td-shell-tab {
    appearance: none;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: var(--space-2-5) var(--space-4);
    color: var(--color-text-secondary);
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .td-shell-tab:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
  }
  .td-shell-tab:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
  .td-shell-tab--active {
    color: var(--color-brand-primary);
    border-bottom-color: var(--color-brand-primary);
  }
  .td-shell-panel {
    min-height: 200px;
  }
  .td-shell-empty {
    margin: 0;
    padding: var(--space-6) var(--space-4);
    text-align: center;
    color: var(--color-text-muted);
  }
  .td-history-title,
  .td-backup-title {
    margin: 0 0 var(--space-3);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
  .td-history-scroll {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
  }
  .td-history-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  .td-history-table th {
    text-align: left;
    padding: var(--space-2-5) var(--space-3);
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    font-weight: 600;
    border-bottom: 1px solid var(--color-border-subtle);
    white-space: nowrap;
  }
  .td-history-table td {
    padding: var(--space-2-5) var(--space-3);
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: middle;
  }
  .td-history-table tbody tr:last-child td {
    border-bottom: none;
  }
  .td-history-table tbody tr:hover td {
    background: var(--color-bg-hover);
  }
  .td-history-progress {
    margin-top: var(--space-1-5);
    min-width: 120px;
  }
  .td-history-phase {
    display: block;
    font-size: 0.75rem;
    color: var(--color-text-secondary);
  }
  .td-history-table-name {
    display: block;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
  }
  .td-history-email {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .td-history-nowrap {
    white-space: nowrap;
  }
  .td-history-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1-5);
  }
  .td-pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--space-3);
    margin-top: var(--space-3);
  }
  .td-pager-showing {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
  }
  .td-pager-controls {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }
  .td-pager-label {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
  }
  .td-backup-text {
    margin: 0 0 var(--space-4);
    color: var(--color-text-secondary);
  }
  .td-backup-actions {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-3);
  }
  .td-backup-progress {
    margin-top: var(--space-4);
  }
  @media (max-width: 640px) {
    .td-shell-header {
      align-items: flex-start;
    }
    .td-shell-header-icon {
      width: 40px;
      height: 40px;
    }
    .td-shell-title {
      font-size: 1.25rem;
    }
    .td-shell-picker {
      max-width: none;
    }
    .td-shell-tab {
      padding: var(--space-2) var(--space-3);
    }
    .td-pager-controls {
      width: 100%;
      justify-content: space-between;
    }
    .td-backup-actions {
      flex-direction: column;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .td-shell *,
    .td-shell-tab {
      animation: none !important;
      transition: none !important;
    }
  }
`;
